import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import {
  fetchPresets,
  setPresetActive,
  type NotificationPreset,
} from '@/features/notifications/api';
import { ApiError } from '@/lib/api/http';

/**
 * 業務マスター > 通知プリセット一覧（D-292）。
 * ルールの新規・編集は独立ページ。モーダルはやめた（ルールが多くて見づらいため）。
 */
export function NotificationPresetsPage(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['notification-presets'], queryFn: fetchPresets });

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(() => {
    const state = location.state as { notice?: string } | null;
    return state?.notice ?? null;
  });
  const [busy, setBusy] = useState(false);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onToggleActive(preset: NotificationPreset): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setPresetActive(preset.id, !preset.is_active);
      await queryClient.invalidateQueries({ queryKey: ['notification-presets'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<NotificationPreset>[] = [
    { key: 'code', header: 'コード', render: (p) => p.code },
    { key: 'name', header: '名称', primary: true, render: (p) => p.name },
    { key: 'rules', header: 'ルール数', render: (p) => `${p.definition.rules?.length ?? 0}件` },
    { key: 'is_active', header: '状態', render: (p) => (p.is_active ? '有効' : '無効化') },
  ];

  return (
    <>
      <PageHeader
        title="通知プリセット"
        description="イベント種別ごとの配信ルールのひな型です。イベント種別マスタでプリセットを選ぶと、その種別のイベント作成時にルールが展開されます。保存しても既存イベントの配信予定は変わりません。"
        actions={
          <Link
            to="/masters/notification-presets/new"
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            新規作成
          </Link>
        }
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {busy && <p className="sr-only">処理中</p>}

      <ListTable<NotificationPreset>
        caption="通知プリセット一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(p) => p.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowActions={[
          {
            label: '編集する',
            onSelect: (p) => navigate(`/masters/notification-presets/${p.id}/edit`),
          },
          { label: '無効化・有効化', onSelect: (p) => void onToggleActive(p) },
        ]}
        emptyTitle="プリセットが登録されていません"
      />
    </>
  );
}
